import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './CreateAgent.css';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaUserPlus, FaEdit, FaTrash, FaTimes, FaUsers,
  FaUserTie, FaCheckCircle, FaTimesCircle, FaChartLine, FaEllipsisV
} from 'react-icons/fa';
import Button from './UI/Button';
import EmployeeProductivity from './EmployeeProductivity';

const MANAGE_ROLES = ['admin', 'e-commerce', 'experience'];
const PRODUCTIVITY_ROLES = ['admin', 'seller', 'logistics', 'logistician', 'eshop', 'employee'];
const ROSTER_WINDOWS = [7, 30, 90];

const dummyAgents = [
  {
    _id: 'exp_agent_1',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    description: 'Senior support specialist with expertise in product returns and customer satisfaction.',
  },
  {
    _id: 'exp_agent_2',
    name: 'Michael Chen',
    email: 'michael.c@example.com',
    description: 'Technical support expert specializing in order processing and shipping issues.',
  },
  {
    _id: 'exp_agent_3',
    name: 'Emma Rodriguez',
    email: 'emma.r@example.com',
    description: 'Customer experience specialist focused on product inquiries and feedback management.',
  }
];

// Demo roster for the "experience" role (same shape as /api/employees/roster-stats)
const dummyRoster = {
  window_days: 7,
  stale_after_days: 3,
  employees: [
    { employee_id: 'exp_agent_1', name: 'Sarah Johnson', email: 'sarah.j@example.com', description: dummyAgents[0].description, open_assigned: 12, stale_open: 2, resolved_window: 18, resolution_rate_window: 92, avg_sentiment_window: 58, ai_validated_share_window: 71, sentiment_n: 14 },
    { employee_id: 'exp_agent_2', name: 'Michael Chen', email: 'michael.c@example.com', description: dummyAgents[1].description, open_assigned: 8, stale_open: 0, resolved_window: 11, resolution_rate_window: 88, avg_sentiment_window: 55, ai_validated_share_window: 64, sentiment_n: 9 },
    { employee_id: 'exp_agent_3', name: 'Emma Rodriguez', email: 'emma.r@example.com', description: dummyAgents[2].description, open_assigned: 5, stale_open: 1, resolved_window: 9, resolution_rate_window: 95, avg_sentiment_window: 62, ai_validated_share_window: 80, sentiment_n: 7 },
  ],
  team: { employees: 3, open_assigned: 25, stale_open: 3, resolved_window: 38, resolution_rate_window: 91, avg_sentiment_window: 58 },
};

const initials = (name) => {
  const words = String(name || '').split(/[\s._-]+/).filter(Boolean);
  if (!words.length) return '?';
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
};

const CreateAgent = () => {
  const { t } = useTranslation();
  const userRole = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}').role || null;
    } catch (err) {
      return null;
    }
  }, []);
  const canManage = MANAGE_ROLES.includes(userRole);
  const canViewProductivity = PRODUCTIVITY_ROLES.includes(userRole);
  const [activeTab, setActiveTab] = useState(canManage ? 'team' : 'productivity');
  const [user, setUser] = useState(undefined);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [externalAdminId, setExternalAdminId] = useState('');
  const [agents, setAgents] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [newDescription, setNewDescription] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newExternalAdminId, setNewExternalAdminId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  // Roster (one batched call — replaces the old per-card stats fan-out)
  const [windowDays, setWindowDays] = useState(7);
  const [roster, setRoster] = useState(null);
  // Row whose "⋯" menu is open
  const [menuFor, setMenuFor] = useState(null);
  // Employee pre-selected when jumping to the Productivité tab from a row
  const [productivityEmployeeId, setProductivityEmployeeId] = useState(null);
  const navigate = useNavigate();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }

    // Roles without team management (productivity-only view) skip the
    // admin-scoped agents/user fetches entirely.
    if (!canManage) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      await fetchUserData(token);
      await fetchAgents(token);
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, canManage]);

  useEffect(() => {
    if (!canManage) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;
    fetchRoster(token, windowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, windowDays]);

  useEffect(() => {
    if (!menuFor) return undefined;
    const close = () => setMenuFor(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuFor]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    }
  };

  const fetchRoster = async (token, days) => {
    if (userRole === 'experience') {
      setRoster({ ...dummyRoster, window_days: days });
      return;
    }
    try {
      const response = await axios.get(`/api/employees/roster-stats?window_days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoster(response.data);
    } catch (error) {
      console.error('Failed to fetch roster stats:', error);
      setRoster(null);
    }
  };

  const fetchAgents = async (token) => {
    try {
      if (userRole === 'experience') {
        setAgents(dummyAgents);
        setIsLoading(false);
        return;
      }

      const response = await axios.get('/api/employees/agents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setIsLoading(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const payload = { name, email, description };

      if (externalAdminId.trim()) {
        payload.external_admin_id = externalAdminId.trim();
      }

      await axios.post(
        '/api/employees/create-agent',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setName('');
      setEmail('');
      setDescription('');
      setExternalAdminId('');
      setCreateModalOpen(false);
      fetchAgents(token);
      fetchRoster(token, windowDays);
      showNotification(t('createAgent.agentCreatedSuccess', 'Agent created successfully'));
    } catch (error) {
      console.error('Failed to create agent:', error);
      showNotification(t('createAgent.createFailed', 'Failed to create agent'), 'error');
    }
  };

  const handleEditDescription = (agent) => {
    setCurrentAgent(agent);
    setNewDescription(agent.description);
    setNewName(agent.name);
    setNewEmail(agent.email);
    setNewExternalAdminId(agent.external_admin_id || '');
    setEditModalOpen(true);
  };

  const handleDeleteAgent = async (agent) => {
    const confirmed = window.confirm(
      t('createAgent.deleteConfirm', 'Supprimer {{name}} ? Ses tickets restent assignés mais le compte ne pourra plus se connecter.', { name: agent.name || agent.email })
    );
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/employees/delete-employee/${agent.email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAgents(token);
      fetchRoster(token, windowDays);
      showNotification(t('createAgent.agentDeletedSuccess', 'Agent deleted successfully'));
    } catch (error) {
      console.error('Failed to delete agent:', error);
      showNotification(t('createAgent.deleteFailed', 'Failed to delete agent'), 'error');
    }
  };

  const handleUpdateDescription = async () => {
    try {
      const token = localStorage.getItem('access_token');

      const updatePayload = {
        email: currentAgent.email,
      };

      if (newName !== currentAgent.name) {
        updatePayload.name = newName;
      }
      if (newEmail !== currentAgent.email) {
        updatePayload.new_email = newEmail;
      }
      if (newDescription !== currentAgent.description) {
        updatePayload.description = newDescription;
      }
      if (newExternalAdminId !== (currentAgent.external_admin_id || '')) {
        updatePayload.external_admin_id = newExternalAdminId.trim();
      }

      await axios.put(
        '/api/employees/update-employee',
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditModalOpen(false);
      fetchAgents(token);
      fetchRoster(token, windowDays);
      showNotification(t('createAgent.updateSuccess', 'Employee updated successfully'));
    } catch (error) {
      console.error('Failed to update employee:', error);
      showNotification(t('createAgent.updateFailed', 'Failed to update employee'), 'error');
    }
  };

  const openProductivityFor = (employeeId) => {
    setProductivityEmployeeId(employeeId);
    setActiveTab('productivity');
  };

  // Roster rows: the stats are keyed by employee id; agents (the editable
  // docs) stay the source of identity so the edit modal always has the full
  // document (external_admin_id included).
  const rosterByEmployee = useMemo(() => {
    const map = {};
    (roster?.employees || []).forEach((row) => { map[row.employee_id] = row; });
    return map;
  }, [roster]);

  if (canManage && (user === undefined || isLoading)) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('createAgent.loadingEmployees')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (canManage && user?.subscription_plan === 'free') {
    return (
      <div className="emp-container">
        <div className="emp-empty-state">
          <FaUsers className="emp-empty-icon" />
          <h3>{t('createAgent.manageEmployees')}</h3>
          <p>{t('createAgent.notAccessible')}</p>
          <Link to="/billing">
            <Button variant="primary">{t('createAgent.upgrade')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const team = roster?.team;

  return (
    <div className="emp-container">
      {notification && (
        <div className={`emp-notification ${notification.type}`}>
          {notification.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="emp-header">
        <div className="emp-header-title">
          <FaUserTie className="emp-header-icon" />
          <h1>{t('createAgent.manageEmployees')}</h1>
        </div>
        {canManage && canViewProductivity && (
          <div className="emp-tabs">
            <button
              type="button"
              className={`emp-tab${activeTab === 'team' ? ' active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              <FaUsers /> {t('createAgent.tabTeam', 'Équipe')}
            </button>
            <button
              type="button"
              className={`emp-tab${activeTab === 'productivity' ? ' active' : ''}`}
              onClick={() => setActiveTab('productivity')}
            >
              <FaChartLine /> {t('createAgent.tabProductivity', 'Productivité')}
            </button>
          </div>
        )}
        {activeTab === 'team' && canManage && (
          <div className="emp-header-actions">
            <div className="emp-window-chips" role="group" aria-label={t('createAgent.windowLabel', 'Période')}>
              {ROSTER_WINDOWS.map((days) => (
                <button
                  key={days}
                  type="button"
                  className={windowDays === days ? 'active' : ''}
                  aria-pressed={windowDays === days}
                  onClick={() => setWindowDays(days)}
                >
                  {days} j
                </button>
              ))}
            </div>
            <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
              <FaUserPlus /> {t('createAgent.createEmployee')}
            </Button>
          </div>
        )}
      </div>

      {activeTab === 'productivity' && canViewProductivity ? (
        <EmployeeProductivity embedded initialEmployeeId={productivityEmployeeId} />
      ) : agents.length === 0 ? (
        <div className="emp-empty-state">
          <FaUsers className="emp-empty-icon" />
          <h3>{t('createAgent.noEmployees', 'No employees yet')}</h3>
          <p>{t('createAgent.noEmployeesDesc', 'Create your first employee to start managing your team')}</p>
          <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
            <FaUserPlus /> {t('createAgent.createEmployee')}
          </Button>
        </div>
      ) : (
        <>
          {team && (
            <div className="emp-summary">
              <div className="emp-sum">
                <span className="emp-sum-label">{t('createAgent.sumEmployees', 'Employés')}</span>
                <span className="emp-sum-value">{team.employees}</span>
              </div>
              <div className="emp-sum">
                <span className="emp-sum-label">{t('createAgent.sumOpen', 'Ouverts assignés')}</span>
                <span className="emp-sum-value">{team.open_assigned}</span>
                {team.stale_open > 0 && (
                  <span className="emp-sum-sub warn">
                    {t('createAgent.sumStale', 'dont {{count}} sans activité > {{days}} j', { count: team.stale_open, days: roster?.stale_after_days ?? 3 })}
                  </span>
                )}
              </div>
              <div className="emp-sum">
                <span className="emp-sum-label">{t('createAgent.sumResolved', 'Résolus · {{days}} j', { days: windowDays })}</span>
                <span className="emp-sum-value">{team.resolved_window}</span>
                {team.resolution_rate_window !== null && team.resolution_rate_window !== undefined && (
                  <span className="emp-sum-sub">{t('createAgent.sumRate', 'taux équipe : {{rate}} %', { rate: team.resolution_rate_window })}</span>
                )}
              </div>
              <div className="emp-sum">
                <span className="emp-sum-label">{t('createAgent.sumMood', 'Humeur clients · {{days}} j', { days: windowDays })}</span>
                <span className="emp-sum-value">
                  {team.avg_sentiment_window !== null && team.avg_sentiment_window !== undefined
                    ? <>{Math.round(team.avg_sentiment_window)}<span className="emp-sum-unit">/100</span></>
                    : '—'}
                </span>
                <span className="emp-sum-sub">{t('createAgent.sumMoodScope', 'sur leurs tickets')}</span>
              </div>
            </div>
          )}

          <div className="emp-roster-card">
            <div className="emp-roster-scroll">
              <table className="emp-roster">
                <thead>
                  <tr>
                    <th>{t('createAgent.colEmployee', 'Employé')}</th>
                    <th>{t('createAgent.colRole', 'Rôle')}</th>
                    <th>{t('createAgent.colLoad', 'Charge actuelle')}</th>
                    <th>{t('createAgent.colResolved', 'Résolus · {{days}} j', { days: windowDays })}</th>
                    <th>{t('createAgent.colMood', 'Humeur · {{days}} j', { days: windowDays })}</th>
                    <th title={t('createAgent.colAiHint', "Part des brouillons IA envoyés tels quels par cet employé")}>
                      {t('createAgent.colAi', 'Validation IA · {{days}} j', { days: windowDays })}
                    </th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => {
                    const row = rosterByEmployee[agent._id] || {};
                    const hasStats = roster !== null;
                    return (
                      <tr key={agent._id}>
                        <td>
                          <div className="emp-cell-identity">
                            <span className="emp-avatar">{initials(agent.name || agent.email)}</span>
                            <div className="emp-cell-lines">
                              <span className="emp-cell-name">{agent.name}</span>
                              <span className="emp-cell-email">{agent.email}</span>
                            </div>
                          </div>
                        </td>
                        <td className="emp-cell-role" title={agent.description || ''}>
                          <span className="emp-role-text">{agent.description || '—'}</span>
                        </td>
                        <td>
                          {hasStats ? (
                            <span className="emp-load">
                              <b>{row.open_assigned ?? 0}</b> {t('createAgent.loadOpen', 'ouverts')}
                              {row.stale_open > 0 ? (
                                <span className="emp-badge warn">{row.stale_open} &gt; {roster?.stale_after_days ?? 3} j</span>
                              ) : (
                                <span className="emp-badge ok">{t('createAgent.loadFresh', 'à jour')}</span>
                              )}
                            </span>
                          ) : '…'}
                        </td>
                        <td className="emp-cell-num">{hasStats ? <b>{row.resolved_window ?? 0}</b> : '…'}</td>
                        <td>
                          {hasStats && row.avg_sentiment_window !== null && row.avg_sentiment_window !== undefined ? (
                            <span className={`emp-mood ${row.avg_sentiment_window <= 39 ? 'low' : row.avg_sentiment_window <= 65 ? 'mid' : 'hi'}`}>
                              <i aria-hidden="true" />{Math.round(row.avg_sentiment_window)}
                            </span>
                          ) : (
                            <span className="emp-muted">—</span>
                          )}
                        </td>
                        <td className="emp-cell-num">
                          {hasStats && row.ai_validated_share_window !== null && row.ai_validated_share_window !== undefined
                            ? `${row.ai_validated_share_window} %`
                            : <span className="emp-muted">—</span>}
                        </td>
                        <td className="emp-cell-actions">
                          {canViewProductivity && (
                            <button type="button" className="emp-link" onClick={() => openProductivityFor(agent._id)}>
                              {t('createAgent.tabProductivity', 'Productivité')}
                            </button>
                          )}
                          {canManage && (
                            <>
                              <button type="button" className="emp-link" onClick={() => handleEditDescription(agent)}>
                                <FaEdit aria-hidden="true" /> {t('createAgent.editShort', 'Modifier')}
                              </button>
                              <span className="emp-more">
                                <button
                                  type="button"
                                  className="emp-link more"
                                  aria-haspopup="menu"
                                  aria-expanded={menuFor === agent._id}
                                  onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === agent._id ? null : agent._id); }}
                                >
                                  <FaEllipsisV />
                                </button>
                                {menuFor === agent._id && (
                                  <div className="emp-more-menu" role="menu">
                                    <button type="button" role="menuitem" className="danger" onClick={() => { setMenuFor(null); handleDeleteAgent(agent); }}>
                                      <FaTrash aria-hidden="true" /> {t('createAgent.deleteEmployee', 'Supprimer')}
                                    </button>
                                  </div>
                                )}
                              </span>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create Modal */}
      {createModalOpen && (
        <div className="emp-modal-overlay" onClick={() => setCreateModalOpen(false)}>
          <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="emp-modal-header">
              <h3>{t('createAgent.createEmployee')}</h3>
              <button className="emp-modal-close" onClick={() => setCreateModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleCreateAgent}>
              <div className="emp-modal-body">
                <div className="emp-form-group">
                  <label>{t('createAgent.name')} *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder={t('createAgent.namePlaceholder', 'Full name')}
                  />
                </div>
                <div className="emp-form-group">
                  <label>{t('createAgent.email')} *</label>
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder={t('createAgent.emailPlaceholder', 'Email address')}
                  />
                </div>
                <div className="emp-form-group">
                  <label>{t('createAgent.description')} *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows="4"
                    placeholder={t('createAgent.descriptionPlaceholder', 'Describe the employee role and expertise...')}
                  />
                </div>
                <div className="emp-form-group">
                  <label>{t('createAgent.externalAdminId')}</label>
                  <input
                    type="text"
                    value={externalAdminId}
                    onChange={(e) => setExternalAdminId(e.target.value)}
                    placeholder={t('createAgent.externalAdminIdPlaceholder')}
                  />
                </div>
              </div>
              <div className="emp-modal-footer">
                <Button type="submit" variant="primary">
                  <FaUserPlus /> {t('createAgent.create')}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setCreateModalOpen(false)}>
                  <FaTimes /> {t('createAgent.cancel')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && currentAgent && (
        <div className="emp-modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="emp-modal-header">
              <h3>{t('createAgent.editEmployee')}</h3>
              <button className="emp-modal-close" onClick={() => setEditModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="emp-modal-body">
              <div className="emp-form-group">
                <label>{t('createAgent.name')} *</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                />
              </div>
              <div className="emp-form-group">
                <label>{t('createAgent.email')} *</label>
                <input
                  type="text"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                />
              </div>
              <div className="emp-form-group">
                <label>{t('createAgent.description')} *</label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  required
                  rows="4"
                />
              </div>
              <div className="emp-form-group">
                <label>{t('createAgent.externalAdminId')}</label>
                <input
                  type="text"
                  value={newExternalAdminId}
                  onChange={(e) => setNewExternalAdminId(e.target.value)}
                  placeholder={t('createAgent.externalAdminIdPlaceholder')}
                />
              </div>
            </div>
            <div className="emp-modal-footer">
              <Button type="button" variant="primary" onClick={handleUpdateDescription}>
                <FaEdit /> {t('createAgent.update')}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditModalOpen(false)}>
                <FaTimes /> {t('createAgent.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAgent;
